import React, { memo, useState, useEffect, useCallback } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { Modal } from 'Components/Modal';
import { Label } from 'Components/Label';
import { PurpleButton } from 'Components/Button';
import { EquipmentAsset, UpdateAssetPayload } from 'Containers/SerializedEquipment/types';

import formClasses from 'Themes/form/form.module.css';
import classes from './editSerializedEquipmentModal.module.css';

interface Props {
  isOpen: boolean;
  asset: EquipmentAsset | null;
  submitting: boolean;
  submitError: string | null;
  onSubmit: (payload: UpdateAssetPayload) => void;
  onClose: (e: any) => void;
}

const EMPTY_FORM = {
  manufacturer: '',
  model: '',
  serialNumber: '',
  assetTag: '',
  vendor: '',
  purchaseDate: '',
  warrantyExpiresAt: '',
  purchasePrice: '',
  rentalDayRate: '',
  note: '',
  status: 'available',
};

type FormState = typeof EMPTY_FORM;

function toDateInput(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function fromAsset(asset: EquipmentAsset | null): FormState {
  if (!asset) return { ...EMPTY_FORM };
  return {
    manufacturer: asset.manufacturer ?? '',
    model: asset.model ?? '',
    serialNumber: asset.serial_number ?? '',
    assetTag: asset.asset_tag ?? '',
    vendor: asset.vendor ?? '',
    purchaseDate: toDateInput(asset.purchase_date),
    warrantyExpiresAt: toDateInput(asset.warranty_expires_at),
    purchasePrice: asset.purchase_price ?? '',
    rentalDayRate: asset.rental_day_rate ?? '',
    note: asset.note ?? '',
    status: asset.status === 'maintenance' ? 'maintenance' : 'available',
  };
}

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (e: any) => void;
}

function Field({ id, label, type = 'text', value, onChange }: FieldProps) {
  return (
    <div className={classes.field}>
      <Label ariaLabel={label} className={formClasses.label} htmlFor={id}>
        {label}
      </Label>
      <input
        id={id}
        type={type}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? '0.01' : undefined}
        className="form-control"
        value={value}
        onChange={onChange}
        autoComplete="off"
        data-testid={id}
      />
    </div>
  );
}

Field.defaultProps = { type: 'text' };

function EditSerializedEquipmentModal({ isOpen, asset, submitting, submitError, onSubmit, onClose }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    if (isOpen) setForm(fromAsset(asset));
  }, [isOpen, asset]);

  const setField = useCallback(
    (field: keyof FormState) => (e: any) => {
      const { value } = e.target;
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const showStatus = !!asset && asset.status !== 'deployed';

  const onSubmitForm = useCallback(
    (e: any) => {
      e.preventDefault();
      if (!asset) return;

      const text = (value: string) => (value.trim() ? value.trim() : null);
      const num = (value: string) => (value.trim() !== '' && !Number.isNaN(Number(value)) ? Number(value) : null);

      const payload: UpdateAssetPayload = {
        updated_at: asset.updated_at,
        manufacturer: text(form.manufacturer),
        model: text(form.model),
        serial_number: text(form.serialNumber),
        asset_tag: text(form.assetTag),
        vendor: text(form.vendor),
        note: text(form.note),
        purchase_date: form.purchaseDate || null,
        warranty_expires_at: form.warrantyExpiresAt || null,
        purchase_price: num(form.purchasePrice),
        rental_day_rate: num(form.rentalDayRate),
      };
      if (showStatus) payload.status = form.status;

      onSubmit(payload);
    },
    [asset, form, showStatus, onSubmit]
  );

  return (
    <Modal
      id="edit-serialized-equipment-modal"
      classes={classes}
      title="Edit Serialized Equipment"
      isOpen={isOpen}
      modalHeader
      dataBsBackdrop="static"
      dataBsKeyboard="false"
      modalCloseClick={onClose}
    >
      <form
        className={`${formClasses.formBase} ${classes.container}`}
        noValidate
        action="#"
        data-testid="edit-serialized-form"
        onSubmit={onSubmitForm}
      >
        <p className={classes.subtitle}>{asset?.name ?? 'Asset'}</p>

        <h6 className={classes.sectionTitle}>Identity</h6>
        <Field id="editManufacturer" label="Manufacturer" value={form.manufacturer} onChange={setField('manufacturer')} />
        <Field id="editModel" label="Model" value={form.model} onChange={setField('model')} />
        <Field id="editSerialNumber" label="Serial Number" value={form.serialNumber} onChange={setField('serialNumber')} />
        <Field id="editAssetTag" label="Asset Tag" value={form.assetTag} onChange={setField('assetTag')} />

        {showStatus && (
          <div className={classes.field}>
            <Label ariaLabel="Status" className={formClasses.label} htmlFor="editStatus">
              Status
            </Label>
            <select
              id="editStatus"
              className="form-select"
              value={form.status}
              aria-label="Status"
              onChange={setField('status')}
            >
              <option value="available">Available</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        )}

        <h6 className={classes.sectionTitle}>Financial / rental</h6>
        <Field id="editVendor" label="Vendor" value={form.vendor} onChange={setField('vendor')} />
        <Field id="editPurchaseDate" label="Purchase Date" type="date" value={form.purchaseDate} onChange={setField('purchaseDate')} />
        <Field id="editPurchasePrice" label="Purchase Price" type="number" value={form.purchasePrice} onChange={setField('purchasePrice')} />
        <Field id="editWarrantyExpiresAt" label="Warranty Expires" type="date" value={form.warrantyExpiresAt} onChange={setField('warrantyExpiresAt')} />
        <Field id="editRentalDayRate" label="Rental Day Rate" type="number" value={form.rentalDayRate} onChange={setField('rentalDayRate')} />
        <Field id="editNote" label="Note" value={form.note} onChange={setField('note')} />

        {submitError && (
          <p className={classes.error} role="alert" data-testid="edit-error">
            {submitError}
          </p>
        )}

        <div className={`modal-footer ${classes.modalFooter}`}>
          <PurpleButton type="button" onClick={onClose} className={classes.cancelButton}>
            Cancel
          </PurpleButton>
          <PurpleButton type="submit" disabled={submitting} className={classes.createButton}>
            Save changes
          </PurpleButton>
        </div>
      </form>
    </Modal>
  );
}

const EditSerializedEquipmentModalMemo = memo(EditSerializedEquipmentModal, areEqual);

export { EditSerializedEquipmentModalMemo as EditSerializedEquipmentModal };
